import { create } from "zustand";
import type { ZoneEditCapture } from "./zoneEditStore"; // type-only → no runtime store cycle

// Multi-field SAMPLING CAMPAIGN: when several fields are selected, Map Soil shows the FIRST field's
// previews but the chosen action applies to EVERY selected field. Two shapes: • PRESET (click a
// preview map / Save & Order the default) → each field's own default design is ordered → N sampling
// work orders (no campaign object needed — see lib/campaignOrder.
export type CampaignMode = "suggest" | "grid" | "zones" | "behavior";
export interface CampaignField {
  field_id: string;
  field_name: string;
  acres?: number;
}

// per-field committed design captured off the active editor when the user navigates / saves
export type Captured =
  | { kind: "points"; points: { num: number; lng: number; lat: number }[] }
  | { kind: "zones"; fc: GeoJSON.FeatureCollection; edit?: ZoneEditCapture };

interface SamplingCampaignState {
  active: boolean;
  mode: CampaignMode | null;
  fields: CampaignField[];
  index: number; // which field the editor is on
  sharedK: number | null; // shared zone count (zones mode) — applies to every field
  captured: Record<string, Captured>; // field_id → the design the user built for that field
  uploads: Record<string, GeoJSON.FeatureCollection>; // field_id → uploaded points (upload+custom: gathered up front)
  awaitingUploads: boolean; // upload+custom: collect ALL fields' uploads before the edit stepper begins

  startCustom: (mode: CampaignMode, fields: CampaignField[]) => void;
  startUploadCustom: (mode: CampaignMode, fields: CampaignField[]) => void;
  beginStepping: () => void; // upload+custom: all uploads gathered → start the editor stepper at field 0
  setIndex: (i: number) => void;
  next: () => void;
  prev: () => void;
  capture: (fieldId: string, c: Captured) => void;
  setSharedK: (k: number) => void;
  addUpload: (fieldId: string, fc: GeoJSON.FeatureCollection) => void;
  end: () => void;
}

type FreshFields = Pick<
  SamplingCampaignState,
  | "active"
  | "mode"
  | "fields"
  | "index"
  | "sharedK"
  | "captured"
  | "uploads"
  | "awaitingUploads"
>;
const fresh = (): FreshFields => ({
  active: false,
  mode: null,
  fields: [],
  index: 0,
  sharedK: null,
  captured: {},
  uploads: {},
  awaitingUploads: false,
});

export const useSamplingCampaign = create<SamplingCampaignState>((set) => ({
  ...fresh(),
  startCustom: (mode, fields) =>
    set({ ...fresh(), active: true, mode, fields }),
  startUploadCustom: (mode, fields) =>
    set({ ...fresh(), active: true, mode, fields, awaitingUploads: true }),
  beginStepping: () => set({ awaitingUploads: false, index: 0 }),
  setIndex: (index) => set({ index }),
  next: () =>
    set((s) => ({ index: Math.min(s.fields.length - 1, s.index + 1) })),
  prev: () => set((s) => ({ index: Math.max(0, s.index - 1) })),
  capture: (fieldId, c) =>
    set((s) => ({ captured: { ...s.captured, [fieldId]: c } })),
  setSharedK: (sharedK) => set({ sharedK }),
  addUpload: (fieldId, fc) =>
    set((s) => ({ uploads: { ...s.uploads, [fieldId]: fc } })),
  end: () => set({ ...fresh() }),
}));

// points vs zones "kind" (drives which save/order path) — behavior classes save+order like zones
export const campaignKind = (m: CampaignMode): "points" | "zones" =>
  m === "suggest" || m === "grid" ? "points" : "zones";

// user never opened in the stepper, and for the preset "run on all fields" path).
export const automapIdForMode = (m: CampaignMode): string =>
  m === "suggest"
    ? "automap-suggested-points"
    : m === "grid"
      ? "automap-grid-points"
      : m === "zones"
        ? "automap-relative-zones"
        : "automap-behavior-zones";

// how to (re)start the point-edit session for a points-mode campaign field
export const pointStartOpts = (
  m: CampaignMode,
): { suggest?: boolean; grid?: boolean } | undefined =>
  m === "suggest"
    ? { suggest: true }
    : m === "grid"
      ? { grid: true }
      : undefined;
